// src/pages/GestionUtilisateurs.jsx
import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usersApi } from '../services/api'

const ROLES = [
  { value: 'super_admin',  label: '👑 Super Admin',   desc: 'Accès total + gestion utilisateurs' },
  { value: 'gestionnaire', label: '🔧 Gestionnaire',  desc: 'Tout sauf gestion utilisateurs' },
  { value: 'commercial',   label: '💼 Commercial',    desc: 'Créer devis/factures, pas de suppression' },
  { value: 'comptable',    label: '📊 Comptable',     desc: 'Lecture seule + validation factures' },
  { value: 'lecture',      label: '👁️ Lecture',       desc: 'Consultation uniquement' },
]

const ROLE_COLORS = {
  super_admin:  { bg: '#7C3AED20', border: '#7C3AED', text: '#C4B5FD' },
  gestionnaire: { bg: '#2563EB20', border: '#2563EB', text: '#93C5FD' },
  commercial:   { bg: '#05966920', border: '#059669', text: '#6EE7B7' },
  comptable:    { bg: '#D9770620', border: '#D97706', text: '#FCD34D' },
  lecture:      { bg: '#64748B20', border: '#64748B', text: '#CBD5E1' },
}

// Miroir de AuthContext — sert à afficher les valeurs par défaut du rôle dans la grille
const PERMISSIONS = {
  super_admin: {
    documents:    { read: true,  write: true,  delete: true  },
    clients:      { read: true,  write: true,  delete: true  },
    fournisseurs: { read: true,  write: true,  delete: true  },
    catalogue:    { read: true,  write: true,  delete: true  },
    bc:           { read: true,  write: true,  delete: true  },
    settings:     { read: true,  write: true                 },
    users:        { read: true,  write: true,  delete: true  },
    import:       { read: true,  write: true                 },
  },
  gestionnaire: {
    documents:    { read: true,  write: true,  delete: true  },
    clients:      { read: true,  write: true,  delete: true  },
    fournisseurs: { read: true,  write: true,  delete: true  },
    catalogue:    { read: true,  write: true,  delete: true  },
    bc:           { read: true,  write: true,  delete: true  },
    settings:     { read: true,  write: true                 },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
  commercial: {
    documents:    { read: true,  write: true,  delete: false },
    clients:      { read: true,  write: true,  delete: false },
    fournisseurs: { read: true,  write: true,  delete: false },
    catalogue:    { read: true,  write: true,  delete: false },
    bc:           { read: true,  write: true,  delete: false },
    settings:     { read: true,  write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
  comptable: {
    documents:    { read: true,  write: false, delete: false },
    clients:      { read: true,  write: false, delete: false },
    fournisseurs: { read: true,  write: false, delete: false },
    catalogue:    { read: true,  write: false, delete: false },
    bc:           { read: true,  write: false, delete: false },
    settings:     { read: true,  write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
  lecture: {
    documents:    { read: true,  write: false, delete: false },
    clients:      { read: true,  write: false, delete: false },
    fournisseurs: { read: true,  write: false, delete: false },
    catalogue:    { read: true,  write: false, delete: false },
    bc:           { read: true,  write: false, delete: false },
    settings:     { read: false, write: false               },
    users:        { read: false, write: false, delete: false },
    import:       { read: false, write: false                },
  },
}

const MODULES = [
  { key: 'documents',    label: '📄 Documents',        hasDelete: true  },
  { key: 'clients',      label: '👥 Clients',           hasDelete: true  },
  { key: 'fournisseurs', label: '🏭 Fournisseurs',      hasDelete: true  },
  { key: 'catalogue',    label: '📦 Catalogue',         hasDelete: true  },
  { key: 'bc',           label: '🛒 Bons de commande',  hasDelete: true  },
  { key: 'settings',     label: '⚙️ Paramètres',        hasDelete: false },
  { key: 'users',        label: '👤 Utilisateurs',      hasDelete: false },
  { key: 'import',       label: '📥 Import / Sauvegarde', hasDelete: false },
]

export default function GestionUtilisateurs() {
  const { profil: moi, isAdmin } = useAuth()
  const [users,            setUsers]            = useState([])
  const [loading,          setLoading]          = useState(true)
  const [showInvite,       setShowInvite]       = useState(false)
  const [invite,           setInvite]           = useState({ email: '', nom: '', role: 'commercial', password: '' })
  const [modeInvite,       setModeInvite]       = useState('email') // 'email' | 'password'
  const [invLoading,       setInvLoading]       = useState(false)
  const [toast,            setToast]            = useState(null)
  const [expandedUserId,   setExpandedUserId]   = useState(null)
  const [permissionsEnCours, setPermissionsEnCours] = useState({})
  const [savingPerm,       setSavingPerm]       = useState(false)
  const [resetUser,        setResetUser]        = useState(null)
  const [resetPwd,         setResetPwd]         = useState({ nouveau: '', confirm: '' })
  const [resetLoading,     setResetLoading]     = useState(false)
  const [resetError,       setResetError]       = useState('')
  const [showResetNouv,    setShowResetNouv]    = useState(false)
  const [showResetConf,    setShowResetConf]    = useState(false)
  const [showInvitePwd,    setShowInvitePwd]    = useState(false)

  const mdpFormatD = (pwd) => {
    if (pwd.length < 12) return 'Minimum 12 caractères'
    if ((pwd.match(/[A-Z]/g)||[]).length < 2) return 'Au moins 2 majuscules'
    if ((pwd.match(/[0-9]/g)||[]).length < 2) return 'Au moins 2 chiffres'
    if (!/[@#$!%*?&]/.test(pwd)) return 'Au moins 1 caractère spécial (@#$!%*?&)'
    if (/(.)\1\1/.test(pwd)) return '3 caractères identiques consécutifs interdits'
    return null
  }
  const mdpForce = (pwd) => {
    let s = 0
    if (pwd.length >= 12) s++; if (pwd.length >= 16) s++
    if ((pwd.match(/[A-Z]/g)||[]).length >= 2) s++
    if ((pwd.match(/[0-9]/g)||[]).length >= 2) s++
    if (/[@#$!%*?&]/.test(pwd)) s++
    if (s <= 2) return { label: 'Faible', color: '#EF4444', pct: 33 }
    if (s <= 3) return { label: 'Moyen', color: '#F59E0B', pct: 60 }
    return { label: 'Fort', color: '#10B981', pct: 100 }
  }
  const genererMdp = () => {
    const U='ABCDEFGHJKLMNPQRSTUVWXYZ', L='abcdefghjkmnpqrstuvwxyz', D='23456789', S='@#$!%*?&'
    const pick = s => s[Math.floor(Math.random()*s.length)]
    const all = U+L+D+S
    let tries = 0
    while (tries++ < 100) {
      let p = pick(U)+pick(U)+pick(D)+pick(D)+pick(S)
      while (p.length < 14) p += pick(all)
      p = p.split('').sort(()=>Math.random()-0.5).join('')
      if (!mdpFormatD(p)) return p
    }
    return 'SOLstice@2026!'
  }

  const handleResetMdp = async () => {
    const err = mdpFormatD(resetPwd.nouveau)
    if (err) { setResetError(err); return }
    if (resetPwd.nouveau !== resetPwd.confirm) { setResetError('Les mots de passe ne correspondent pas.'); return }
    setResetLoading(true); setResetError('')
    try {
      await usersApi.reinitialiserMotDePasse(resetUser.id, resetPwd.nouveau)
      notify('success', `Mot de passe de ${resetUser.nom} réinitialisé.`)
      setResetUser(null); setResetPwd({ nouveau: '', confirm: '' })
      setShowResetNouv(false); setShowResetConf(false)
    } catch (err) { setResetError('Erreur : ' + err.message) }
    finally { setResetLoading(false) }
  }

  const notify = (type, text) => {
    setToast({ type, text })
    setTimeout(() => setToast(null), 4000)
  }

  useEffect(() => { charger() }, [])

  const charger = async () => {
    setLoading(true)
    try { setUsers(await usersApi.liste()) }
    catch { notify('error', 'Impossible de charger les utilisateurs.') }
    finally { setLoading(false) }
  }

  const handleInviter = async (e) => {
    e.preventDefault()
    setInvLoading(true)
    try {
      if (modeInvite === 'password') {
        const errMdp = mdpFormatD(invite.password)
        if (errMdp) { notify('error', 'Mot de passe : ' + errMdp); setInvLoading(false); return }
        await usersApi.inviter(invite.email, invite.nom, invite.role, invite.password)
        notify('success', `Compte créé pour ${invite.email}`)
      } else {
        await usersApi.inviterParMail(invite.email, invite.nom, invite.role)
        notify('success', `Invitation envoyée à ${invite.email}`)
      }
      setInvite({ email: '', nom: '', role: 'commercial', password: '' })
      setShowInvite(false)
      charger()
    } catch (err) {
      notify('error', err.message || "Erreur lors de l'invitation.")
    } finally { setInvLoading(false) }
  }

  const handleRole = async (id, role) => {
    try {
      await usersApi.changerRole(id, role)
      setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u))
      notify('success', 'Rôle mis à jour.')
    } catch { notify('error', 'Erreur changement de rôle.') }
  }

  const handleDesactiver = async (id) => {
    if (!window.confirm('Désactiver cet utilisateur ?')) return
    try {
      await usersApi.desactiver(id)
      setUsers(prev => prev.map(u => u.id === id ? { ...u, actif: false } : u))
    } catch { notify('error', 'Erreur.') }
  }

  const handleSupprimer = async (u) => {
    if (!window.confirm(`Supprimer définitivement ${u.nom} (${u.email}) ?\n\nCette action est irréversible.`)) return
    try {
      await usersApi.supprimer(u.id)
      setUsers(prev => prev.filter(x => x.id !== u.id))
      notify('success', `${u.nom} supprimé.`)
    } catch (err) { notify('error', 'Erreur suppression : ' + err.message) }
  }

  // Ouvrir / fermer le panneau d'accès d'un utilisateur
  const handleToggleAcces = (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null)
      return
    }
    setExpandedUserId(userId)
    const user = users.find(u => u.id === userId)
    setPermissionsEnCours(prev => ({
      ...prev,
      [userId]: JSON.parse(JSON.stringify(user?.permissions_custom || {}))
    }))
  }

  // Basculer une permission dans le draft
  const handleTogglePermission = (userId, resource, action) => {
    const user = users.find(u => u.id === userId)
    const roleDefault = PERMISSIONS[user.role]?.[resource]?.[action] ?? false
    const draft = permissionsEnCours[userId] || {}
    const currentCustom = draft[resource]?.[action]
    const currentEffective = currentCustom !== undefined ? currentCustom : roleDefault
    const newValue = !currentEffective

    setPermissionsEnCours(prev => {
      const newDraft = JSON.parse(JSON.stringify(prev[userId] || {}))
      if (newValue === roleDefault) {
        // Même valeur que le rôle → supprimer l'exception
        if (newDraft[resource]) {
          delete newDraft[resource][action]
          if (Object.keys(newDraft[resource]).length === 0) delete newDraft[resource]
        }
      } else {
        // Valeur différente du rôle → stocker l'exception
        if (!newDraft[resource]) newDraft[resource] = {}
        newDraft[resource][action] = newValue
      }
      return { ...prev, [userId]: newDraft }
    })
  }

  // Sauvegarder les permissions d'un utilisateur
  const handleSauvegarder = async (userId) => {
    setSavingPerm(true)
    try {
      const draft = permissionsEnCours[userId] || {}
      const updated = await usersApi.mettreAJourPermissions(userId, draft)
      setUsers(prev => prev.map(u => u.id === userId ? updated : u))
      notify('success', 'Accès enregistrés.')
      setExpandedUserId(null)
    } catch {
      notify('error', 'Erreur lors de la sauvegarde.')
    } finally {
      setSavingPerm(false)
    }
  }

  // Réinitialiser toutes les exceptions d'un utilisateur (retour au rôle pur)
  const handleResetPermissions = async (userId) => {
    if (!window.confirm('Réinitialiser tous les accès personnalisés ? L\'utilisateur suivra uniquement son rôle.')) return
    setSavingPerm(true)
    try {
      const updated = await usersApi.mettreAJourPermissions(userId, {})
      setUsers(prev => prev.map(u => u.id === userId ? updated : u))
      setPermissionsEnCours(prev => ({ ...prev, [userId]: {} }))
      notify('success', 'Accès réinitialisés.')
    } catch {
      notify('error', 'Erreur lors de la réinitialisation.')
    } finally {
      setSavingPerm(false)
    }
  }

  // Vérifie si un utilisateur a des accès personnalisés (exceptions au rôle)
  const aDesAccesPersonnalises = (user) =>
    user.permissions_custom && Object.keys(user.permissions_custom).length > 0

  if (!isAdmin()) return (
    <div style={{ padding: '2rem', color: '#EF4444', textAlign: 'center', fontFamily: "'Bricolage Grotesque', sans-serif" }}>
      🚫 Accès réservé aux administrateurs.
    </div>
  )

  const inp = (extra = {}) => ({
    padding: '0.8rem 1rem', borderRadius: '10px',
    border: '1.5px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.05)', color: 'white',
    fontSize: '0.9rem', outline: 'none', width: '100%', boxSizing: 'border-box',
    ...extra
  })

  // Rendu de la grille de permissions pour un utilisateur
  const renderPanneauAcces = (user) => {
    const draft = permissionsEnCours[user.id] || {}
    const hasChanges = JSON.stringify(draft) !== JSON.stringify(user.permissions_custom || {})

    return (
      <tr key={`panel-${user.id}`}>
        <td colSpan={6} style={{ padding: 0, borderTop: 'none' }}>
          <div style={{
            margin: '0 1rem 1rem',
            background: 'rgba(255,255,255,0.03)',
            border: '1px solid rgba(255,107,53,0.2)',
            borderRadius: 14,
            padding: '1.25rem 1.5rem',
          }}>
            {/* En-tête du panneau */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#E2E8F0' }}>
                  🔐 Configuration des accès — {user.nom} {user.prenom || ''}
                </span>
                <div style={{ color: '#64748B', fontSize: '0.75rem', marginTop: '0.2rem' }}>
                  Cases grises = hérité du rôle · Cases orange = exception personnalisée
                </div>
              </div>
              <button
                onClick={() => handleResetPermissions(user.id)}
                disabled={savingPerm || !aDesAccesPersonnalises(user)}
                style={{
                  background: 'rgba(239,68,68,0.1)',
                  border: '1px solid rgba(239,68,68,0.3)',
                  color: aDesAccesPersonnalises(user) ? '#FCA5A5' : '#4B5563',
                  borderRadius: 8, padding: '0.35rem 0.85rem',
                  cursor: aDesAccesPersonnalises(user) ? 'pointer' : 'not-allowed',
                  fontSize: '0.78rem', fontWeight: 600,
                }}
              >
                🔄 Réinitialiser
              </button>
            </div>

            {/* Grille des permissions */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', width: '40%' }}>
                      Module
                    </th>
                    {['Consulter', 'Créer / Modifier', 'Supprimer'].map(col => (
                      <th key={col} style={{ textAlign: 'center', padding: '0.5rem 0.75rem', color: '#64748B', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map(mod => {
                    const actions = [
                      { key: 'read',   label: 'Consulter'       },
                      { key: 'write',  label: 'Créer / Modifier' },
                      { key: 'delete', label: 'Supprimer'        },
                    ]
                    return (
                      <tr key={mod.key} style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                        <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.875rem', fontWeight: 500, color: '#CBD5E1' }}>
                          {mod.label}
                        </td>
                        {actions.map(action => {
                          // Supprimer non applicable pour settings
                          if (action.key === 'delete' && !mod.hasDelete) {
                            return (
                              <td key={action.key} style={{ textAlign: 'center', padding: '0.6rem 0.75rem' }}>
                                <span style={{ color: '#334155', fontSize: '0.8rem' }}>—</span>
                              </td>
                            )
                          }

                          const roleDefault = PERMISSIONS[user.role]?.[mod.key]?.[action.key] ?? false
                          const customVal   = draft[mod.key]?.[action.key]
                          const isCustom    = customVal !== undefined
                          const effective   = isCustom ? customVal : roleDefault

                          return (
                            <td key={action.key} style={{ textAlign: 'center', padding: '0.6rem 0.75rem' }}>
                              <div
                                onClick={() => handleTogglePermission(user.id, mod.key, action.key)}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                  width: 28, height: 28, borderRadius: 7, cursor: 'pointer',
                                  border: isCustom
                                    ? `2px solid ${effective ? '#FF6B35' : '#FF6B3560'}`
                                    : `2px solid ${effective ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.08)'}`,
                                  background: isCustom
                                    ? (effective ? 'rgba(255,107,53,0.25)' : 'rgba(255,107,53,0.05)')
                                    : (effective ? 'rgba(255,255,255,0.1)' : 'transparent'),
                                  transition: 'all 0.15s ease',
                                  userSelect: 'none',
                                }}
                                title={`${action.label} — ${isCustom ? 'Exception personnalisée' : 'Hérité du rôle'}`}
                              >
                                {effective
                                  ? <span style={{ fontSize: 14, color: isCustom ? '#FF6B35' : '#94A3B8' }}>✓</span>
                                  : <span style={{ fontSize: 12, color: isCustom ? '#FF6B3560' : '#334155'  }}>✗</span>
                                }
                              </div>
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Boutons d'action */}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={() => setExpandedUserId(null)}
                style={{
                  background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                  color: '#94A3B8', borderRadius: 10, padding: '0.6rem 1.25rem',
                  cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => handleSauvegarder(user.id)}
                disabled={savingPerm || !hasChanges}
                style={{
                  background: hasChanges && !savingPerm
                    ? 'linear-gradient(135deg, #FF6B35, #E55A2B)'
                    : 'rgba(255,107,53,0.2)',
                  border: 'none', borderRadius: 10, padding: '0.6rem 1.5rem',
                  color: hasChanges && !savingPerm ? 'white' : '#64748B',
                  cursor: hasChanges && !savingPerm ? 'pointer' : 'not-allowed',
                  fontSize: '0.85rem', fontWeight: 700,
                  boxShadow: hasChanges && !savingPerm ? '0 4px 14px rgba(255,107,53,0.3)' : 'none',
                }}
              >
                {savingPerm ? '⏳ Enregistrement…' : '💾 Enregistrer les accès'}
              </button>
            </div>
          </div>
        </td>
      </tr>
    )
  }

  return (
    <div style={{ padding: '2rem', fontFamily: "'Bricolage Grotesque', sans-serif", color: 'white' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 80, right: 20, zIndex: 99999,
          background: toast.type === 'success' ? 'rgba(16,185,129,0.95)' : 'rgba(239,68,68,0.95)',
          color: 'white', borderRadius: 12, padding: '0.75rem 1.25rem',
          fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        }}>
          {toast.type === 'success' ? '✅' : '⚠️'} {toast.text}
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>👥 Utilisateurs</h2>
          <p style={{ margin: '0.25rem 0 0', color: '#64748B', fontSize: '0.85rem' }}>
            {users.filter(u => u.actif).length} actif(s) · {users.length} total
          </p>
        </div>
        <button
          onClick={() => setShowInvite(v => !v)}
          style={{
            background: 'linear-gradient(135deg,#FF6B35,#E55A2B)',
            border: 'none', borderRadius: 12, padding: '0.7rem 1.25rem',
            color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem',
          }}>
          {showInvite ? '✕ Annuler' : '+ Inviter'}
        </button>
      </div>

      {/* Formulaire invitation */}
      {showInvite && (
        <div style={{ marginBottom: '1.5rem' }}>
          {/* Toggle mode */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            {[
              { val: 'email',    label: '✉️ Invitation par email', desc: "L'utilisateur reçoit un lien et définit son mot de passe" },
              { val: 'password', label: '🔒 Création directe',     desc: "L'admin définit le mot de passe et le communique" },
            ].map(m => (
              <button key={m.val} type="button" onClick={() => setModeInvite(m.val)}
                title={m.desc}
                style={{
                  padding: '0.5rem 1rem', borderRadius: 10, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
                  border: modeInvite === m.val ? '1.5px solid #FF6B35' : '1px solid rgba(255,255,255,0.1)',
                  background: modeInvite === m.val ? 'rgba(255,107,53,0.15)' : 'rgba(255,255,255,0.04)',
                  color: modeInvite === m.val ? '#FF6B35' : '#64748B',
                }}>
                {m.label}
              </button>
            ))}
            <span style={{ color: '#475569', fontSize: '0.78rem', alignSelf: 'center', marginLeft: '0.5rem' }}>
              {modeInvite === 'email'
                ? "L'utilisateur reçoit un email avec un lien pour définir son mot de passe"
                : "Le compte est créé immédiatement — communiquer le mot de passe à l'utilisateur"}
            </span>
          </div>

          <form onSubmit={handleInviter} style={{
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16, padding: '1.5rem',
            display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end',
          }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nom</label>
              <input value={invite.nom} required placeholder="Jean Dupont"
                onChange={e => setInvite(p => ({ ...p, nom: e.target.value }))} style={inp()} />
            </div>
            <div style={{ flex: '1 1 180px' }}>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</label>
              <input type="email" value={invite.email} required placeholder="jean@entreprise.cm"
                onChange={e => setInvite(p => ({ ...p, email: e.target.value }))} style={inp()} />
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Rôle</label>
              <select value={invite.role} onChange={e => setInvite(p => ({ ...p, role: e.target.value }))}
                style={inp({ background: '#1E293B' })}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {modeInvite === 'password' && (
              <div style={{ flex: '1 1 100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {/* Règles + Générateur */}
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                    <strong style={{ color: '#FF6B35' }}>Format D :</strong> ≥12 car. · 2 maj. · 2 chiffres · 1 spécial (@#$!%*?&)
                  </span>
                  <button type="button"
                    onClick={() => { const g = genererMdp(); setInvite(p => ({ ...p, password: g })) }}
                    style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.35)',
                      color: '#C4B5FD', borderRadius: 8, padding: '0.3rem 0.75rem',
                      cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                    ✨ Générer
                  </button>
                </div>
                {/* Champ mot de passe */}
                <div style={{ position: 'relative' }}>
                  <input
                    type={showInvitePwd ? 'text' : 'password'}
                    value={invite.password}
                    required={modeInvite === 'password'}
                    placeholder="Mot de passe Format D"
                    onChange={e => setInvite(p => ({ ...p, password: e.target.value }))}
                    style={inp({ paddingRight: '2.5rem',
                      borderColor: invite.password
                        ? mdpFormatD(invite.password) ? 'rgba(239,68,68,0.5)' : 'rgba(16,185,129,0.5)'
                        : undefined })} />
                  <span onClick={() => setShowInvitePwd(v => !v)}
                    style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                      cursor: 'pointer', fontSize: '0.9rem', color: '#64748B' }}>
                    {showInvitePwd ? '🙈' : '👁️'}
                  </span>
                </div>
                {/* Barre de force + erreur */}
                {invite.password && (() => {
                  const f = mdpForce(invite.password)
                  const err = mdpFormatD(invite.password)
                  return (
                    <div>
                      <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${f.pct}%`, background: f.color, transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.2rem' }}>
                        <span style={{ fontSize: '0.7rem', color: f.color, fontWeight: 700 }}>{f.label}</span>
                        {err && <span style={{ fontSize: '0.7rem', color: '#FCA5A5' }}>⚠️ {err}</span>}
                      </div>
                    </div>
                  )
                })()}
              </div>
            )}
            <div style={{ flex: '0 0 auto' }}>
              <button type="submit" disabled={invLoading} style={{
                padding: '0.8rem 1.25rem', background: modeInvite === 'email' ? '#2563EB' : '#10B981', border: 'none',
                borderRadius: 10, color: 'white', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
                fontSize: '0.875rem',
              }}>
                {invLoading ? '⏳ …' : modeInvite === 'email' ? '✉️ Envoyer l\'invitation' : '➕ Créer le compte'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748B' }}>Chargement…</div>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 820, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                {['Utilisateur', 'Email', 'Rôle', 'Statut', 'Accès', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '0.9rem 1rem', textAlign: 'left', color: '#64748B',
                    fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.05em', whiteSpace: 'nowrap',
                    minWidth: h === 'Actions' ? 220 : h === 'Email' ? 180 : 'auto',
                  }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const rc = ROLE_COLORS[u.role] || ROLE_COLORS.lecture
                const isMe = u.id === moi?.id
                const isExpanded = expandedUserId === u.id
                const personnalise = aDesAccesPersonnalises(u)

                return (
                  <React.Fragment key={u.id}>
                    <tr style={{
                      borderTop: '1px solid rgba(255,255,255,0.05)',
                      background: isExpanded ? 'rgba(255,107,53,0.04)' : 'transparent',
                    }}>
                      <td style={{ padding: '0.9rem 1rem', fontWeight: 600 }}>
                        {u.nom} {u.prenom || ''}
                        {isMe && <span style={{ color: '#00D4AA', fontSize: '0.7rem', marginLeft: 6 }}>(moi)</span>}
                      </td>
                      <td style={{ padding: '0.9rem 1rem', color: '#94A3B8', fontSize: '0.875rem' }}>{u.email}</td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        {isMe ? (
                          <span style={{ background: rc.bg, border: `1px solid ${rc.border}`, color: rc.text, padding: '0.25rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700 }}>
                            {ROLES.find(r => r.value === u.role)?.label || u.role}
                          </span>
                        ) : (
                          <select value={u.role} onChange={e => handleRole(u.id, e.target.value)}
                            style={{ background: rc.bg, border: `1px solid ${rc.border}`, color: rc.text, padding: '0.25rem 0.75rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', outline: 'none' }}>
                            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        <span style={{
                          background: u.actif ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                          color: u.actif ? '#10B981' : '#EF4444',
                          padding: '0.2rem 0.65rem', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                        }}>
                          {u.actif ? '● Actif' : '● Inactif'}
                        </span>
                      </td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        {!isMe ? (
                          <button
                            onClick={() => handleToggleAcces(u.id)}
                            style={{
                              background: isExpanded
                                ? 'rgba(255,107,53,0.2)'
                                : personnalise ? 'rgba(255,107,53,0.12)' : 'rgba(0,212,170,0.1)',
                              border: `1px solid ${isExpanded
                                ? 'rgba(255,107,53,0.5)'
                                : personnalise ? 'rgba(255,107,53,0.35)' : 'rgba(0,212,170,0.35)'}`,
                              color: isExpanded ? '#FF6B35' : personnalise ? '#FF6B35' : '#00D4AA',
                              padding: '0.25rem 0.75rem', borderRadius: 20,
                              fontSize: '0.72rem', fontWeight: 700,
                              cursor: 'pointer', whiteSpace: 'nowrap',
                            }}
                          >
                            {isExpanded ? '▲ ' : '▼ '}
                            {personnalise ? '✦ Personnalisé' : 'Par rôle'}
                          </button>
                        ) : (
                          <span style={{
                            color: '#334155', fontSize: '0.72rem', fontWeight: 600,
                            padding: '0.25rem 0.75rem',
                          }}>
                            —
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.9rem 1rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {!isMe && moi?.role === 'super_admin' && (
                            <button onClick={() => { setResetUser(u); setResetPwd({ nouveau: '', confirm: '' }); setResetError(''); setShowResetNouv(false); setShowResetConf(false) }}
                              title="Réinitialiser le mot de passe"
                              style={{
                                background: 'rgba(124,58,237,0.12)', border: '1px solid rgba(124,58,237,0.3)',
                                color: '#C4B5FD', borderRadius: 8, padding: '0.35rem 0.75rem',
                                cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                              }}>
                              🔑 Réinit.
                            </button>
                          )}
                          {!isMe && u.actif && (
                            <button onClick={() => handleDesactiver(u.id)} style={{
                              background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                              color: '#FCA5A5', borderRadius: 8, padding: '0.35rem 0.75rem',
                              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                            }}>
                              Désactiver
                            </button>
                          )}
                          {!isMe && moi?.role === 'super_admin' && u.role !== 'super_admin' && (
                            <button onClick={() => handleSupprimer(u)} style={{
                              background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.5)',
                              color: '#F87171', borderRadius: 8, padding: '0.35rem 0.75rem',
                              cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700,
                            }}>
                              🗑️ Supprimer
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && renderPanneauAcces(u)}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Légende rôles */}
      <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: '0.6rem' }}>
        {ROLES.map(r => {
          const rc = ROLE_COLORS[r.value]
          return (
            <div key={r.value} style={{ background: rc.bg, border: `1px solid ${rc.border}30`, borderRadius: 10, padding: '0.7rem 0.9rem' }}>
              <div style={{ color: rc.text, fontWeight: 700, fontSize: '0.85rem' }}>{r.label}</div>
              <div style={{ color: '#64748B', fontSize: '0.75rem', marginTop: '0.2rem' }}>{r.desc}</div>
            </div>
          )
        })}
      </div>

      {/* ══ MODAL RÉINITIALISATION MOT DE PASSE ══════════════════════════════ */}
      {resetUser && (() => {
        const errFormat = resetPwd.nouveau ? mdpFormatD(resetPwd.nouveau) : null
        const force = mdpForce(resetPwd.nouveau)
        const matchOk = resetPwd.nouveau && resetPwd.confirm && resetPwd.nouveau === resetPwd.confirm
        const inpStyle = (borderColor) => ({
          width: '100%', padding: '0.8rem 2.5rem 0.8rem 1rem', borderRadius: 10,
          border: `1.5px solid ${borderColor}`, background: 'rgba(255,255,255,0.05)',
          color: 'white', fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
        })
        return (
          <div onClick={e => { if (e.target === e.currentTarget) { setResetUser(null) } }}
            style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#0F172A', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 20, width: '100%', maxWidth: 460, padding: '2rem',
              boxShadow: '0 25px 80px rgba(0,0,0,0.6)', fontFamily: "'Bricolage Grotesque',sans-serif" }}>

              <h3 style={{ margin: '0 0 0.35rem', fontSize: '1.1rem', fontWeight: 800, color: 'white' }}>
                🔑 Réinitialiser le mot de passe
              </h3>
              <p style={{ margin: '0 0 1.5rem', color: '#64748B', fontSize: '0.85rem' }}>
                Utilisateur : <strong style={{ color: '#E2E8F0' }}>{resetUser.nom}</strong> — {resetUser.email}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Règles Format D */}
                <div style={{ background: 'rgba(255,107,53,0.06)', border: '1px solid rgba(255,107,53,0.2)',
                  borderRadius: 10, padding: '0.75rem 1rem', fontSize: '0.78rem', color: '#94A3B8', lineHeight: 1.6 }}>
                  <strong style={{ color: '#FF6B35' }}>Format D requis :</strong> ≥12 car. · 2 majuscules · 2 chiffres · 1 spécial (@#$!%*?&) · pas de 3 identiques consécutifs
                </div>

                {/* Générateur */}
                <button onClick={() => { const g = genererMdp(); setResetPwd({ nouveau: g, confirm: g }); setResetError('') }}
                  style={{ background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.35)',
                    color: '#C4B5FD', borderRadius: 10, padding: '0.55rem 1rem', cursor: 'pointer',
                    fontSize: '0.82rem', fontWeight: 700, textAlign: 'left' }}>
                  ✨ Générer un mot de passe sécurisé
                </button>

                {/* Nouveau mot de passe */}
                <div>
                  <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Nouveau mot de passe</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showResetNouv ? 'text' : 'password'} value={resetPwd.nouveau}
                      placeholder="Nouveau mot de passe"
                      onChange={e => { setResetPwd(p => ({ ...p, nouveau: e.target.value })); setResetError('') }}
                      style={inpStyle(errFormat ? 'rgba(239,68,68,0.5)' : resetPwd.nouveau && !errFormat ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.1)')} />
                    <span onClick={() => setShowResetNouv(v => !v)}
                      style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: '0.9rem', color: '#64748B' }}>
                      {showResetNouv ? '🙈' : '👁️'}
                    </span>
                  </div>
                  {resetPwd.nouveau && (
                    <div style={{ marginTop: '0.4rem' }}>
                      <div style={{ height: 4, borderRadius: 4, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${force.pct}%`, background: force.color, transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.25rem' }}>
                        <span style={{ fontSize: '0.72rem', color: force.color, fontWeight: 700 }}>{force.label}</span>
                        {errFormat && <span style={{ fontSize: '0.72rem', color: '#FCA5A5' }}>⚠️ {errFormat}</span>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirmer */}
                <div>
                  <label style={{ display: 'block', color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Confirmer le mot de passe</label>
                  <div style={{ position: 'relative' }}>
                    <input type={showResetConf ? 'text' : 'password'} value={resetPwd.confirm}
                      placeholder="Répéter le mot de passe"
                      onChange={e => { setResetPwd(p => ({ ...p, confirm: e.target.value })); setResetError('') }}
                      style={inpStyle(resetPwd.confirm && !matchOk ? 'rgba(239,68,68,0.5)' : matchOk ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.1)')} />
                    <span onClick={() => setShowResetConf(v => !v)}
                      style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', fontSize: '0.9rem', color: '#64748B' }}>
                      {showResetConf ? '🙈' : '👁️'}
                    </span>
                  </div>
                  {resetPwd.confirm && !matchOk && <p style={{ margin: '0.25rem 0 0', color: '#FCA5A5', fontSize: '0.72rem' }}>⚠️ Les mots de passe ne correspondent pas</p>}
                  {matchOk && <p style={{ margin: '0.25rem 0 0', color: '#10B981', fontSize: '0.72rem' }}>✓ Les mots de passe correspondent</p>}
                </div>

                {resetError && <p style={{ margin: 0, color: '#FCA5A5', fontSize: '0.82rem' }}>⚠️ {resetError}</p>}

                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                  <button onClick={() => setResetUser(null)}
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
                      color: '#94A3B8', borderRadius: 10, padding: '0.65rem 1.25rem', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                    Annuler
                  </button>
                  <button onClick={handleResetMdp} disabled={resetLoading || !!errFormat || !matchOk}
                    style={{ background: resetLoading || errFormat || !matchOk ? 'rgba(255,107,53,0.2)' : 'linear-gradient(135deg,#FF6B35,#E55A2B)',
                      border: 'none', borderRadius: 10, padding: '0.65rem 1.5rem',
                      color: resetLoading || errFormat || !matchOk ? '#64748B' : 'white',
                      cursor: resetLoading || errFormat || !matchOk ? 'not-allowed' : 'pointer',
                      fontSize: '0.85rem', fontWeight: 700 }}>
                    {resetLoading ? '⏳ En cours…' : '💾 Enregistrer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
